import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Override Leaflet's default marker styles
const leafletOverrides = document.createElement('style');
leafletOverrides.textContent = `
  .leaflet-marker-icon {
    background: none !important;
    border: none !important;
  }
`;
document.head.appendChild(leafletOverrides);
import {
  ArrowLeft,
  RefreshCw,
  Eye,
  EyeOff,
  Store,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { api } from '../../services/api';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color: string) => {
  return L.divIcon({
    className: '', // Empty to avoid Leaflet default divIcon styles
    html: `<div style="
      width: 24px;
      height: 24px;
      background-color: ${color};
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

const storeIcons = {
  assigned: createCustomIcon('#3b82f6'),     // blue
  unassigned: createCustomIcon('#ef4444'),   // red
  multiAssigned: createCustomIcon('#f97316'), // orange (if ever needed)
};

// Seed circle colors by status
const seedColors: Record<string, string> = {
  active: '#22c55e',    // green
  paused: '#eab308',    // yellow
  inactive: '#9ca3af',  // gray
};

// Convert miles to meters for Leaflet
const milesToMeters = (miles: number) => miles * 1609.34;

interface MapSeed {
  id: number;
  name: string;
  country: string;
  status: string;
  center_latitude: number;
  center_longitude: number;
  radius_miles: number;
  store_count: number;
}

interface MapStore {
  id: string;
  name: string;
  city: string;
  state: string;
  country: string;
  latitude: number;
  longitude: number;
  is_active: boolean;
  assigned_seed_id: number | null;
}

// Component to fit map bounds to markers
function FitBounds({ seeds, stores }: { seeds: MapSeed[]; stores: MapStore[] }) {
  const map = useMap();

  useEffect(() => {
    const allCoords: [number, number][] = [];

    seeds.forEach(seed => {
      if (seed.center_latitude && seed.center_longitude) {
        allCoords.push([seed.center_latitude, seed.center_longitude]);
      }
    });

    stores.forEach(store => {
      if (store.latitude && store.longitude) {
        allCoords.push([store.latitude, store.longitude]);
      }
    });

    if (allCoords.length > 0) {
      const bounds = L.latLngBounds(allCoords);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, seeds, stores]);

  return null;
}

export default function SeedMap() {
  const [showSeeds, setShowSeeds] = useState(true);
  const [showStores, setShowStores] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(false); // false = show ALL stores
  const [selectedSeed, setSelectedSeed] = useState<number | null>(null);
  const [countryFilter, setCountryFilter] = useState<string>('');

  const queryClient = useQueryClient();

  // Fetch map data
  const { data: mapData, isLoading, isError } = useQuery({
    queryKey: ['admin-seed-map'],
    queryFn: () => api.getAdminSeedMapData(),
  });

  // Auto-assign mutation
  const autoAssignMutation = useMutation({
    mutationFn: () => api.autoAssignAdminSeeds(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-seed-map'] });
      queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
      queryClient.invalidateQueries({ queryKey: ['admin-seed-stats'] });
      alert(`Auto-assignment complete!\n\nAssigned: ${data.assigned_count} stores\nUnassigned: ${data.unassigned_count} stores`);
    },
  });

  // Filter data by country
  const filteredData = useMemo(() => {
    if (!mapData) return { seeds: [], stores: [], unassigned_store_ids: [] };

    let seeds = mapData.seeds;
    let stores = mapData.stores;

    if (countryFilter) {
      seeds = seeds.filter(s => s.country === countryFilter);
      stores = stores.filter(s => s.country === countryFilter);
    }

    if (selectedSeed) {
      stores = stores.filter(s => s.assigned_seed_id === selectedSeed);
    }

    return {
      seeds,
      stores,
      unassigned_store_ids: mapData.unassigned_store_ids,
    };
  }, [mapData, countryFilter, selectedSeed]);

  // Calculate stats
  const stats = useMemo(() => {
    if (!mapData) return { total: 0, assigned: 0, unassigned: 0 };
    const total = mapData.stores.length;
    const unassigned = mapData.unassigned_store_ids.length;
    return {
      total,
      assigned: total - unassigned,
      unassigned,
    };
  }, [mapData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-red-600">
        <AlertCircle size={48} className="mb-4" />
        <p>Failed to load map data</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/seeds"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Seed Browsers
        </Link>

        <div className="flex items-center gap-3">
          <button
            onClick={() => autoAssignMutation.mutate()}
            disabled={autoAssignMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={autoAssignMutation.isPending ? 'animate-spin' : ''} />
            Auto-Assign Stores
          </button>
        </div>
      </div>

      {/* Stats & Controls */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Stats */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Store className="text-gray-400" size={18} />
              <span className="text-sm">
                <strong>{stats.total}</strong> stores
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="text-green-500" size={18} />
              <span className="text-sm">
                <strong>{stats.assigned}</strong> covered
              </span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="text-red-500" size={18} />
              <span className="text-sm">
                <strong>{stats.unassigned}</strong> uncovered
              </span>
            </div>
          </div>

          {/* Filters & Toggles */}
          <div className="flex items-center gap-4">
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Countries</option>
              <optgroup label="North America">
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="MX">Mexico</option>
              </optgroup>
              <optgroup label="Europe">
                <option value="DE">Germany</option>
                <option value="GB">United Kingdom</option>
                <option value="FR">France</option>
                <option value="SE">Sweden</option>
                <option value="NO">Norway</option>
                <option value="DK">Denmark</option>
                <option value="FI">Finland</option>
                <option value="NL">Netherlands</option>
                <option value="BE">Belgium</option>
                <option value="AT">Austria</option>
                <option value="CH">Switzerland</option>
                <option value="ES">Spain</option>
                <option value="IT">Italy</option>
                <option value="PL">Poland</option>
              </optgroup>
              <optgroup label="Asia Pacific">
                <option value="JP">Japan</option>
                <option value="KR">South Korea</option>
                <option value="CN">China</option>
                <option value="AU">Australia</option>
                <option value="IN">India</option>
              </optgroup>
              <optgroup label="Middle East">
                <option value="AE">UAE</option>
                <option value="SA">Saudi Arabia</option>
              </optgroup>
            </select>

            <button
              onClick={() => setShowSeeds(!showSeeds)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${
                showSeeds
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}
            >
              {showSeeds ? <Eye size={14} /> : <EyeOff size={14} />}
              Seed Circles
            </button>

            <button
              onClick={() => setShowStores(!showStores)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${
                showStores
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}
            >
              {showStores ? <Eye size={14} /> : <EyeOff size={14} />}
              Stores
            </button>

            <button
              onClick={() => setShowUnassigned(!showUnassigned)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${
                showUnassigned
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}
            >
              {showUnassigned ? <Eye size={14} /> : <EyeOff size={14} />}
              Uncovered Only
            </button>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 280px)' }}>
        <MapContainer
          center={[39.8283, -98.5795]} // Center of US
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          maxBounds={[[-90, -180], [90, 180]]}
          maxBoundsViscosity={1.0}
          minZoom={2}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <FitBounds seeds={filteredData.seeds} stores={filteredData.stores} />

          {/* Seed Coverage Circles */}
          {showSeeds && filteredData.seeds.map((seed) => (
            <Circle
              key={`seed-${seed.id}`}
              center={[seed.center_latitude, seed.center_longitude]}
              radius={milesToMeters(seed.radius_miles)}
              pathOptions={{
                color: seedColors[seed.status] || seedColors.inactive,
                fillColor: seedColors[seed.status] || seedColors.inactive,
                fillOpacity: selectedSeed === seed.id ? 0.3 : 0.15,
                weight: selectedSeed === seed.id ? 3 : 2,
              }}
              eventHandlers={{
                click: () => setSelectedSeed(selectedSeed === seed.id ? null : seed.id),
              }}
            >
              <Popup>
                <div className="font-medium">{seed.name}</div>
                <div className="text-sm text-gray-500">
                  {seed.country} | {seed.radius_miles} mi radius
                </div>
                <div className="text-sm">
                  <span className={`capitalize ${
                    seed.status === 'active' ? 'text-green-600' :
                    seed.status === 'paused' ? 'text-yellow-600' : 'text-gray-500'
                  }`}>
                    {seed.status}
                  </span>
                  {' | '}{seed.store_count} stores
                </div>
                <button
                  onClick={() => setSelectedSeed(selectedSeed === seed.id ? null : seed.id)}
                  className="mt-2 text-xs text-primary-600 hover:underline"
                >
                  {selectedSeed === seed.id ? 'Show all stores' : 'Show only this seed\'s stores'}
                </button>
              </Popup>
            </Circle>
          ))}

          {/* Store Markers */}
          {showStores && filteredData.stores.map((store) => {
            // Skip stores with invalid coordinates
            if (!store.latitude || !store.longitude ||
                (store.latitude === 0 && store.longitude === 0)) return null;

            // Skip if filtering by unassigned and store is assigned
            if (showUnassigned && store.assigned_seed_id) return null;

            const isUnassigned = !store.assigned_seed_id;
            const icon = isUnassigned ? storeIcons.unassigned : storeIcons.assigned;

            return (
              <Marker
                key={`store-${store.id}`}
                position={[store.latitude, store.longitude]}
                icon={icon}
              >
                <Popup>
                  <div className="font-medium">{store.name}</div>
                  <div className="text-sm text-gray-500">
                    {store.city}, {store.state}
                  </div>
                  <div className="text-sm text-gray-500">
                    ID: {store.id}
                  </div>
                  <div className={`text-sm mt-1 ${
                    isUnassigned ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {isUnassigned ? 'Not covered by any seed' : `Covered by seed #${store.assigned_seed_id}`}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Legend</h3>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow"></div>
            <span className="text-sm text-gray-600">Covered Store</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow"></div>
            <span className="text-sm text-gray-600">Uncovered Store</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-3 rounded-full bg-green-500/30 border-2 border-green-500"></div>
            <span className="text-sm text-gray-600">Active Seed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-3 rounded-full bg-yellow-500/30 border-2 border-yellow-500"></div>
            <span className="text-sm text-gray-600">Paused Seed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-3 rounded-full bg-gray-400/30 border-2 border-gray-400"></div>
            <span className="text-sm text-gray-600">Inactive Seed</span>
          </div>
        </div>
      </div>
    </div>
  );
}
